// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A timeline segment component for visualizing asset shipments.
 * This module provides an interactive visual representation of
 * shipping events, including origin, destination, and status
 * indicators within a timeline context. The component supports
 * various states and interactions to provide a rich user experience
 * for tracking shipment progress.
 *
 * Key features:
 * - Interactive timeline segments
 * - Visual state indicators
 * - Date range visualization
 * - Warning indicators
 * - Gradient effects
 * - Click-to-edit support
 * - Tooltip integration
 * - Accessibility support
 */
import { Asset, EventGroup } from '@/asset/api/types';
import { EventGroupDialog } from '@/asset/components/event-group-dialog';
import { useTimeline } from '@/contexts/timeline-context';
import { useDisclosure } from '@/hooks/use-disclosure';
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import { alpha, useTheme } from '@mui/material';
import { AxisScale } from '@visx/axis';
import { LinearGradient } from '@visx/gradient';
import { BarRounded } from '@visx/shape';
import { Text } from '@visx/text';
import { maxBy, minBy } from 'lodash-es';
import { DateTime } from 'luxon';
import { MouseEventHandler, useId } from 'react';

/**
 * Types of warnings that can be displayed on a timeline segment.
 * Used to highlight potential issues with origin or destination.
 *
 * Values:
 * - 'origin': Indicates an issue with the shipment origin
 * - 'destination': Indicates an issue with the shipment destination
 */
export type AssetShipmentTimelineSegmentWarning = 'origin' | 'destination';

/**
 * Determines the start date of an event group.
 * Looks for the earliest event in the sequence and returns
 * either its actual or expected date. Prioritizes actual dates
 * over expected dates for accurate timeline representation.
 *
 * @param shipment - Event group representing an asset shipment
 * @returns The start date or null if no valid date exists
 *
 * @example
 * ```ts
 * const shipment = {
 *   events: [
 *     { sequence: 1, occurredAt: DateTime.now(), expectedAt: null },
 *     { sequence: 2, occurredAt: null, expectedAt: DateTime.now().plus({ days: 1 }) }
 *   ]
 * };
 * const startDate = getStartDateTime(shipment); // Returns first event's occurredAt
 * ```
 */
function getStartDateTime(shipment: EventGroup) {
  const event = minBy(shipment.events, (event) => event.sequence);
  return event?.occurredAt ?? event?.expectedAt ?? null;
}

/**
 * Determines the end date of an event group.
 * Looks for the latest event in the sequence and returns
 * either its actual or expected date. Prioritizes actual dates
 * over expected dates for accurate timeline representation.
 *
 * @param shipment - Event group representing an asset shipment
 * @returns The end date or null if no valid date exists
 *
 * @example
 * ```ts
 * const shipment = {
 *   events: [
 *     { sequence: 1, occurredAt: DateTime.now(), expectedAt: null },
 *     { sequence: 2, occurredAt: null, expectedAt: DateTime.now().plus({ days: 1 }) }
 *   ]
 * };
 * const endDate = getEndDateTime(shipment); // Returns second event's expectedAt
 * ```
 */
function getEndDateTime(shipment: EventGroup) {
  const event = maxBy(shipment.events, (event) => event.sequence);
  return event?.occurredAt ?? event?.expectedAt ?? null;
}

/**
 * Props for configuring the AssetShipmentTimelineSegment component.
 * @typedef {Object} Props
 */
interface Props {
  /**
   * Vertical position (y coordinate) of the segment in the timeline.
   * Used for proper placement in relation to other segments.
   * Value is in pixels from the top of the timeline.
   */
  y: number;

  /**
   * Height of the segment bar in pixels.
   * Controls the visual weight of the segment.
   * Typically matches the timeline row height.
   */
  height: number;

  /**
   * Asset information required for segment display and interaction.
   * Used for tooltips and edit dialogs. Only requires essential
   * properties to minimize data requirements.
   *
   * @example
   * ```ts
   * const asset = {
   *   key: 'asset-123',
   *   serial: 'SN123456',
   *   currentLocation: { name: 'Warehouse A' },
   *   inventoryDate: DateTime.now()
   * };
   * ```
   */
  asset: Asset;

  /**
   * Whether the segment can be edited by clicking.
   * When true, opens an edit dialog on click.
   * Used to control user permissions and interaction states.
   * @default false
   */
  isEditable?: boolean;

  /**
   * Time scale for positioning events on the timeline.
   * Maps dates to pixel positions. Used by visx for
   * proper segment placement and sizing.
   */
  scale: AxisScale<number>;

  /**
   * Event group data representing the shipment.
   * Contains all events and metadata for the segment.
   * Used to determine segment appearance and behavior.
   *
   * @example
   * ```ts
   * const shipment = {
   *   origin: { name: 'Warehouse A' },
   *   destination: { name: 'Customer Site' },
   *   events: [
   *     {
   *       sequence: 1,
   *       occurredAt: DateTime.now(),
   *       expectedAt: null
   *     }
   *   ]
   * };
   * ```
   */
  shipment: EventGroup;

  /**
   * Array of warning indicators to display.
   * Highlights potential issues with origin or destination.
   * Affects text color and visual indicators.
   *
   * @example ['origin', 'destination']
   */
  warnings?: AssetShipmentTimelineSegmentWarning[];

  /**
   * Whether the segment represents an invalid state.
   * Affects visual styling to indicate problems.
   * Adds a border and changes the segment color.
   */
  isInvalid?: boolean;

  /**
   * Earliest date for the segment.
   * Used for proper date range display and gradient effects
   * when start date is missing.
   */
  minimumDateTime: DateTime;

  /**
   * Latest date for the segment.
   * Used for proper date range display and gradient effects
   * when end date is missing.
   */
  maximumDateTime: DateTime;

  /**
   * Handler for segment click events.
   * Called when the segment is clicked.
   * Provides mouse event details for custom handling.
   */
  onClick?: MouseEventHandler<SVGElement>;

  /**
   * Handler for mouse movement over the segment.
   * Used for tooltip positioning and hover effects.
   * Called continuously during mouse movement.
   */
  onMouseMove?: MouseEventHandler<SVGElement>;

  /**
   * Handler for mouse leaving the segment.
   * Used for tooltip hiding and hover state cleanup.
   * Called when mouse exits the segment bounds.
   */
  onMouseLeave?: MouseEventHandler<SVGElement>;
}

/**
 * A visual segment component for the asset timeline.
 * Renders an interactive bar representing a shipment event group,
 * with support for editing, warnings, and status indicators.
 *
 * Features:
 * - Interactive editing through click-to-edit dialog
 * - Visual indicators for expected vs actual dates
 * - Origin and destination labels with warning states
 * - Warning highlights for potential issues
 * - Gradient effects for incomplete date ranges
 * - Invalid state visualization with borders
 * - Mouse interaction support for tooltips
 * - Accessibility support through ARIA attributes
 *
 * Visual States:
 * - Normal: Solid color for confirmed shipments
 * - Expected: Diagonal pattern for planned shipments
 * - Invalid: Border highlight for problem shipments
 * - Warning: Colored text for origin/destination issues
 * - Gradient: Fade effect for incomplete date ranges
 *
 * Interactions:
 * - Click to edit (when enabled)
 * - Hover for tooltip
 * - Mouse tracking for tooltip positioning
 *
 * Usage example:
 * ```tsx
 * const shipment = {
 *   origin: { name: 'Warehouse A' },
 *   destination: { name: 'Customer Site' },
 *   events: [
 *     {
 *       sequence: 1,
 *       occurredAt: DateTime.now(),
 *       expectedAt: null
 *     }
 *   ]
 * };
 *
 * return (
 *   <AssetShipmentTimelineSegment
 *     y={100}
 *     height={30}
 *     scale={timeScale}
 *     asset={asset}
 *     shipment={shipment}
 *     isEditable={true}
 *     warnings={['destination']}
 *     minimumDateTime={DateTime.now().minus({ days: 30 })}
 *     maximumDateTime={DateTime.now().plus({ days: 30 })}
 *     onMouseMove={handleMouseMove}
 *   />
 * );
 * ```
 *
 * @param props - Component configuration options
 */
export function AssetShipmentTimelineSegment(props: Props) {
  const theme = useTheme();
  const dialog = useDisclosure();
  const gradientId = useId();
  const { palette } = useTimeline();

  const startDateTime = getStartDateTime(props.shipment);
  const endDateTime = getEndDateTime(props.shipment);

  if (startDateTime === null && endDateTime === null) {
    return null;
  }

  // Neighbor-derived fallbacks can land on the wrong side of this group's own date
  const start =
    startDateTime ??
    DateTime.min(props.minimumDateTime, endDateTime ?? props.maximumDateTime);
  const end =
    endDateTime ??
    DateTime.max(props.maximumDateTime, startDateTime ?? props.minimumDateTime);
  const isExpected = props.shipment.events.some(
    ({ occurredAt, expectedAt }) => occurredAt === null && expectedAt !== null
  );

  const x = props.scale(start) ?? 0;
  const width = Math.max(3, (props.scale(end) ?? 0) - x);
  const stroke = props.isInvalid ? palette.invalid.solid : undefined;
  const stokeWidth = props.isInvalid ? 2 : undefined;
  const cursor =
    props.onClick === undefined && !props.isEditable ? 'default' : 'pointer';

  const originTextX = x - 4;
  const destinationTextX = x + width + 4;
  const textY = props.y + props.height / 2;
  const gradientFrom =
    startDateTime === null
      ? alpha(palette.asset.solid, 0.15)
      : palette.asset.solid;

  const gradientTo =
    endDateTime === null
      ? alpha(palette.asset.solid, 0.15)
      : palette.asset.solid;

  let fill = isExpected ? palette.asset.diagonal : palette.asset.solid;

  if (startDateTime === null || endDateTime === null) {
    fill = `url(#${gradientId})`;
  }

  const handleClick: MouseEventHandler<SVGElement> = (event) => {
    if (props.isEditable) {
      dialog.open();
    }

    props.onClick?.(event);
  };

  const originTextColor = props.warnings?.includes('origin')
    ? theme.palette.warning.dark
    : theme.palette.text.primary;

  const destinationTextColor = props.warnings?.includes('destination')
    ? theme.palette.warning.dark
    : theme.palette.text.primary;

  return (
    <>
      {props.isEditable && (
        <EventGroupDialog
          asset={props.asset}
          isOpen={dialog.isOpen}
          onClose={dialog.close}
          eventGroup={props.shipment}
          configuration={props.shipment.configuration}
        />
      )}
      <g
        onClick={handleClick}
        onMouseMove={props.onMouseMove}
        onMouseLeave={props.onMouseLeave}
        style={{ cursor }}
      >
        <LinearGradient
          id={gradientId}
          vertical={false}
          from={gradientFrom}
          to={gradientTo}
        />
        <Text
          x={originTextX}
          y={textY}
          fill={originTextColor}
          style={theme.typography.caption}
          textAnchor="end"
          verticalAnchor="middle"
        >
          {props.shipment.origin
            ? getEffectiveLocationName(props.shipment.origin)
            : undefined}
        </Text>
        <BarRounded
          all
          radius={4}
          x={x}
          y={props.y}
          fill={fill}
          width={width}
          height={props.height}
          stroke={stroke}
          strokeWidth={stokeWidth}
        />
        <Text
          x={destinationTextX}
          y={textY}
          fill={destinationTextColor}
          style={theme.typography.caption}
          verticalAnchor="middle"
        >
          {props.shipment.destination
            ? getEffectiveLocationName(props.shipment.destination)
            : undefined}
        </Text>
      </g>
    </>
  );
}
